import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Autocomplete,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  InputAdornment,
  IconButton,
  Chip,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  CircularProgress,
  ListItemSecondaryAction,
  Switch,
  Collapse,
  Checkbox,
  Tooltip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Add as AddIcon,
  Place as PlaceIcon,
  FilterList as FilterListIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

// Firebase imports removed
// import { collection, getDocs, query, where, orderBy } from 'firebase-shim';
// import { db } from '../../firebase/config';

// Component imports
import NewClientDialog from '../dialogs/NewClientDialog';

// Define interfaces
interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  client: string;
  clientId: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state: string;
  };
  deliveryDetails?: {
    name: string;
    address: string;
    city?: string;
    state?: string;
    pincode?: string;
    phone?: string;
    email?: string;
  };
  items: any[];
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxable: boolean;
  userId: string;
  createdAt: string;
}

interface ClientSelectionStepProps {
  invoiceData: InvoiceData;
  updateInvoiceData: (data: Partial<InvoiceData>) => void;
  userId: string;
  clients?: any[];
  onClientAdded?: (client: any) => void; // Callback when a new client is added
}

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  gstin?: string;
  state: string;
  city?: string;
  pincode?: string;
  billingFrequency?: number;
  type?: 'business' | 'individual';
  isFavorite?: boolean;
  userId?: string;
  createdAt?: string;
}

// Check the type for the NewClientDialog component
interface NewClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (client: any) => void;
  client: any;
  userId: string;
}

const ClientSelectionStep: React.FC<ClientSelectionStepProps> = ({
  invoiceData,
  updateInvoiceData,
  userId,
  clients = [],
  onClientAdded
}) => {
  // State for client selection
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredClients, setFilteredClients] = useState<Client[]>(clients);
  const [frequentClients, setFrequentClients] = useState<Client[]>([]);
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [clientTypeFilter, setClientTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('frequency');

  // State for delivery details
  const [sameAsClient, setSameAsClient] = useState(true);
  const [deliveryDetails, setDeliveryDetails] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: ''
  });

  // Effect to load and sort clients
  useEffect(() => {
    if (clients && clients.length > 0) {
      // Sort by billing frequency first
      const sorted = [...clients].sort((a, b) => {
        return (b.billingFrequency || 0) - (a.billingFrequency || 0);
      });

      setFilteredClients(sorted);

      // Get top 5 frequent clients
      const frequent = sorted.slice(0, 5);
      setFrequentClients(frequent);
    } else {
    }
  }, [clients]);

  // Effect to set selected client if invoiceData already has a client
  useEffect(() => {
    if (invoiceData.clientId && clients.length > 0) {
      const existingClient = clients.find(client => client.id === invoiceData.clientId);
      if (existingClient) {
        setSelectedClient(existingClient);

        // If delivery details are not already set, use client details
        if (sameAsClient || !invoiceData.deliveryDetails) {
          setDeliveryDetails({
            name: existingClient.name || '',
            address: existingClient.address || '',
            city: existingClient.city || '',
            state: existingClient.state || '',
            pincode: existingClient.pincode || '',
            phone: existingClient.phone || '',
            email: existingClient.email || ''
          });
        }
      }
    }
  }, [invoiceData.clientId, clients]);

  // Additional debug logging for client data
  useEffect(() => {
    if (clients && Array.isArray(clients)) {
      if (clients.length > 0) {
        // Check if first client has expected properties
        const firstClient = clients[0];
      }
    } else {
      console.warn('ClientSelectionStep: clients prop is not an array or is undefined');
    }
  }, [clients]);

  // Handle client selection
  const handleClientSelect = (client: Client | null) => {
    setSelectedClient(client);

    if (client) {
      // Update invoice data with selected client
      updateInvoiceData({
        client: client.name,
        clientId: client.id,
        clientDetails: {
          address: client.address || '',
          gstin: client.gstin || '',
          email: client.email || '',
          phone: client.phone || '',
          state: client.state || ''
        }
      });

      // Update delivery details if sameAsClient is true
      if (sameAsClient) {
        const newDeliveryDetails = {
          name: client.name || '',
          address: client.address || '',
          city: client.city || '',
          state: client.state || '',
          pincode: client.pincode || '',
          phone: client.phone || '',
          email: client.email || ''
        };

        setDeliveryDetails(newDeliveryDetails);
        updateInvoiceData({ deliveryDetails: newDeliveryDetails });
      }
    } else {
      // Clear client data if no client selected
      updateInvoiceData({
        client: '',
        clientId: '',
        clientDetails: {
          address: '',
          gstin: '',
          email: '',
          phone: '',
          state: ''
        }
      });
    }
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    if (query.trim() === '') {
      // Reset to original sorted list if search is cleared
      const sorted = [...clients].sort((a, b) => {
        if (sortBy === 'frequency') {
          return (b.billingFrequency || 0) - (a.billingFrequency || 0);
        } else if (sortBy === 'name') {
          return (a.name || '').localeCompare(b.name || '');
        }
        return 0;
      });

      // Apply type filter if set
      let filtered = sorted;
      if (clientTypeFilter !== 'all') {
        filtered = sorted.filter(client => client.type === clientTypeFilter);
      }

      setFilteredClients(filtered);
    } else {
      // Filter by search query
      const filtered = clients.filter(client => {
        const nameMatch = (client.name || '').toLowerCase().includes(query);
        const emailMatch = (client.email || '').toLowerCase().includes(query);
        const phoneMatch = (client.phone || '').toLowerCase().includes(query);
        const gstinMatch = (client.gstin || '').toLowerCase().includes(query);

        return nameMatch || emailMatch || phoneMatch || gstinMatch;
      });

      // Apply type filter if set
      let finalFiltered = filtered;
      if (clientTypeFilter !== 'all') {
        finalFiltered = filtered.filter(client => client.type === clientTypeFilter);
      }

      setFilteredClients(finalFiltered);
    }
  };

  // Handle filter changes
  const handleFilterChange = (type: string) => {
    setClientTypeFilter(type);

    let filtered = clients;
    // Apply search filter first
    if (searchQuery) {
      filtered = clients.filter(client => {
        const nameMatch = (client.name || '').toLowerCase().includes(searchQuery);
        const emailMatch = (client.email || '').toLowerCase().includes(searchQuery);
        const phoneMatch = (client.phone || '').toLowerCase().includes(searchQuery);
        const gstinMatch = (client.gstin || '').toLowerCase().includes(searchQuery);

        return nameMatch || emailMatch || phoneMatch || gstinMatch;
      });
    }

    // Then apply type filter
    if (type !== 'all') {
      filtered = filtered.filter(client => client.type === type);
    }

    // Finally sort
    filtered.sort((a, b) => {
      if (sortBy === 'frequency') {
        return (b.billingFrequency || 0) - (a.billingFrequency || 0);
      } else if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      return 0;
    });

    setFilteredClients(filtered);
  };

  // Handle sort change
  const handleSortChange = (sortType: string) => {
    setSortBy(sortType);

    const sorted = [...filteredClients].sort((a, b) => {
      if (sortType === 'frequency') {
        return (b.billingFrequency || 0) - (a.billingFrequency || 0);
      } else if (sortType === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      return 0;
    });

    setFilteredClients(sorted);
  };

  // Handle adding a new client
  const handleAddNewClient = (newClient: any) => {
    // Close dialog
    setNewClientDialogOpen(false);

    // Create a proper Client object
    const client: Client = {
      id: newClient.id,
      name: newClient.name || '',
      email: newClient.email || '',
      phone: newClient.phone || '',
      address: newClient.address || '',
      gstin: newClient.gstin || '',
      state: newClient.state || '',
      city: newClient.city || '',
      pincode: newClient.pincode || '',
      type: (newClient.type || 'individual') as 'business' | 'individual',
      userId: newClient.userId || userId,
      createdAt: newClient.createdAt || new Date().toISOString(),
      billingFrequency: 0
    };

    // Add the new client to the local clients list
    setFilteredClients(prev => {
      // Check if client already exists
      if (prev.some(c => c.id === client.id)) {
        return prev.map(c => c.id === client.id ? client : c);
      }
      return [...prev, client];
    });

    // Notify parent component that a new client was added
    if (onClientAdded) {
      onClientAdded(client);
    }

    // Set the new client as selected
    handleClientSelect(client);
  };

  // Handle sameAsClient toggle
  const handleSameAsClientToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSameAsClient(checked);

    if (checked && selectedClient) {
      // Copy client details to delivery details
      const newDeliveryDetails = {
        name: selectedClient.name || '',
        address: selectedClient.address || '',
        city: selectedClient.city || '',
        state: selectedClient.state || '',
        pincode: selectedClient.pincode || '',
        phone: selectedClient.phone || '',
        email: selectedClient.email || ''
      };

      setDeliveryDetails(newDeliveryDetails);
      updateInvoiceData({ deliveryDetails: newDeliveryDetails });
    }
  };

  // Handle delivery details change
  const handleDeliveryDetailsChange = (field: string, value: string) => {
    const updatedDetails = {
      ...deliveryDetails,
      [field]: value
    };

    setDeliveryDetails(updatedDetails);
    updateInvoiceData({ deliveryDetails: updatedDetails });
  };

  // Render client item
  const renderClientItem = (client: Client) => {
    const isHighFrequency = (client.billingFrequency || 0) > 3;

    return (
      <ListItem
        disablePadding
        key={client.id}
        sx={{ mb: 1 }}
        secondaryAction={
          isHighFrequency && (
            <Tooltip title="Frequently billed client">
              <StarIcon color="primary" fontSize="small" />
            </Tooltip>
          )
        }
      >
        <ListItemButton
          onClick={() => handleClientSelect(client)}
          selected={selectedClient?.id === client.id}
          sx={{
            borderRadius: 1,
            backgroundColor: selectedClient?.id === client.id ? 'primary.light' : 'transparent',
            '&:hover': {
              backgroundColor: selectedClient?.id === client.id ? 'primary.light' : 'action.hover',
            }
          }}
        >
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: client.type === 'business' ? 'info.main' : 'success.main' }}>
              {client.type === 'business' ? <BusinessIcon /> : <PersonIcon />}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography variant="subtitle1" fontWeight={500}>
                {client.name}
                {client.gstin && (
                  <Chip
                    size="small"
                    label="GST"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    color="info"
                  />
                )}
              </Typography>
            }
            secondary={
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <PhoneIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem', opacity: 0.7 }} />
                  {client.phone || 'No phone'}
                </Typography>
                <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <LocationOnIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem', opacity: 0.7 }} />
                  {client.address ?
                    (client.address.length > 30 ? client.address.substring(0, 30) + '...' : client.address) :
                    'No address'
                  }
                </Typography>
                {client.email && (
                  <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem', opacity: 0.7 }} />
                    {client.email}
                  </Typography>
                )}
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Client Selection Section */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Select Client
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                size="small"
                onClick={() => setNewClientDialogOpen(true)}
                sx={{ borderRadius: 2 }}
              >
                New Client
              </Button>
            </Box>

            {/* Search and Filters */}
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    placeholder="Search clients by name, email, phone or GSTIN"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowFilterOptions(!showFilterOptions)}
                            color={showFilterOptions ? "primary" : "default"}
                          >
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                {/* Filter options */}
                <Grid item xs={12}>
                  <Collapse in={showFilterOptions}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Filter Options
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setShowFilterOptions(false)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl component="fieldset" size="small">
                            <FormLabel component="legend">Client Type</FormLabel>
                            <RadioGroup
                              row
                              value={clientTypeFilter}
                              onChange={(e) => handleFilterChange(e.target.value)}
                            >
                              <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                              <FormControlLabel value="business" control={<Radio size="small" />} label="Business" />
                              <FormControlLabel value="individual" control={<Radio size="small" />} label="Individual" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl component="fieldset" size="small">
                            <FormLabel component="legend">Sort By</FormLabel>
                            <RadioGroup
                              row
                              value={sortBy}
                              onChange={(e) => handleSortChange(e.target.value)}
                            >
                              <FormControlLabel value="frequency" control={<Radio size="small" />} label="Most Frequent" />
                              <FormControlLabel value="name" control={<Radio size="small" />} label="Name" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Collapse>
                </Grid>
              </Grid>
            </Box>

            {/* Frequently billed clients section */}
            {frequentClients.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <StarIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                  Frequently Billed Clients
                </Typography>
                <List disablePadding>
                  {frequentClients.map(client => renderClientItem(client))}
                </List>
              </Box>
            )}

            {/* All clients list */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                All Clients {clients.length > 0 ? `(${clients.length})` : ''}
              </Typography>
              {filteredClients.length > 0 ? (
                <List disablePadding>
                  {filteredClients.map(client => renderClientItem(client))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No clients match your search.' :
                      clients.length > 0 ? 'No clients match the current filters.' : 'No clients found.'}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setNewClientDialogOpen(true)}
                    sx={{ mt: 1, borderRadius: 2 }}
                    size="small"
                  >
                    Add New Client
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Delivery Details Section */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Delivery Details
            </Typography>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sameAsClient}
                    onChange={handleSameAsClientToggle}
                    color="primary"
                  />
                }
                label="Same as client details"
              />

              {selectedClient && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    setSameAsClient(true);
                    handleSameAsClientToggle({ target: { checked: true } } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  sx={{ ml: 1, fontSize: '0.8rem' }}
                  disabled={sameAsClient}
                >
                  Copy from client
                </Button>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Recipient Name"
                  value={deliveryDetails.name}
                  onChange={(e) => handleDeliveryDetailsChange('name', e.target.value)}
                  disabled={sameAsClient}
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={deliveryDetails.address}
                  onChange={(e) => handleDeliveryDetailsChange('address', e.target.value)}
                  disabled={sameAsClient}
                  multiline
                  rows={3}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={deliveryDetails.city}
                  onChange={(e) => handleDeliveryDetailsChange('city', e.target.value)}
                  disabled={sameAsClient}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={deliveryDetails.state}
                  onChange={(e) => handleDeliveryDetailsChange('state', e.target.value)}
                  disabled={sameAsClient}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={deliveryDetails.pincode}
                  onChange={(e) => handleDeliveryDetailsChange('pincode', e.target.value)}
                  disabled={sameAsClient}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={deliveryDetails.phone}
                  onChange={(e) => handleDeliveryDetailsChange('phone', e.target.value)}
                  disabled={sameAsClient}
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={deliveryDetails.email}
                  onChange={(e) => handleDeliveryDetailsChange('email', e.target.value)}
                  disabled={sameAsClient}
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* New Client Dialog */}
      <NewClientDialog
        open={newClientDialogOpen}
        onClose={() => setNewClientDialogOpen(false)}
        onSave={handleAddNewClient}
        client={null}
        userId={userId}
      />
    </Box>
  );
};

export default ClientSelectionStep; 
